/**
 * Site header (components/header/render.php, styles in header.css next to it).
 *
 * Opening and closing the panel is @phucbm/data-toggle's (the hamburger's
 * data-toggle="is-menu-open,is-prevent-scroll"); this file adds what a menu
 * needs on top: focus into the panel and back to the hamburger, the rest of
 * the page inert while open (keyboard and screen readers stay in the menu),
 * close on a link click or when the window grows past the breakpoint, submenu
 * buttons, the language list, the measured header height, scroll classes.
 */

import type {DataToggleEventDetail, DataToggleInstance} from '@phucbm/data-toggle';
import {createScrollDirection} from '@phucbm/scroll-direction';

const MENU_CLASSES = 'is-menu-open,is-prevent-scroll';
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

const isVisible = (el: Element): boolean => el.getClientRects().length > 0;

export function initHeader(toggle: DataToggleInstance): void {
    const header = document.getElementById('px-header');
    if (!header) return;
    const panel = document.getElementById('px-mobile-menu');
    const hamburger = header.querySelector<HTMLElement>('.px-hamburger');
    const html = document.documentElement;

    const isOpen = () => html.classList.contains('is-menu-open');
    const close = () => toggle.toggle(MENU_CLASSES, undefined, false);

    // Header height for the panel's top and scroll offsets: --px-header-height.
    new ResizeObserver(() => {
        html.style.setProperty('--px-header-height', `${Math.round(header.getBoundingClientRect().height)}px`);
    }).observe(header);

    // is-scroll-top|middle|bottom, is-scroll-up|down on <body>; "top" = within the header's height, "bottom" =
    // the last half screen. header.css: the bar hides in the middle, back on scroll up (on the desktop front
    // page only with Theme Options' switch on, round 1 #2), and the transparent header turns white past the top.
    createScrollDirection({topOffset: () => header.offsetHeight, bottomOffset: () => window.innerHeight * 0.5});

    initSubmenus();
    initSubmenuPill();
    initLanguage();

    if (!panel || !hamburger) return;

    const panelFocusables = () => Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isVisible);

    // While open, everything else on the page is inert: no focus, no clicks, hidden from screen readers.
    // The bar stays live (its hamburger closes the menu); only elements made inert here are restored.
    // Header and panel can sit inside a wrapper (block templates: <div class="wp-site-blocks">), so walk
    // up from both to <body> and make only the siblings at each level inert, never an ancestor of them.
    const madeInert: HTMLElement[] = [];
    const setPageInert = (inert: boolean) => {
        if (inert) {
            const keep = new Set<Element>();
            [header, panel].forEach(start => {
                for (let el: Element | null = start; el && el !== document.body; el = el.parentElement) keep.add(el);
            });
            const parents = new Set(Array.from(keep, el => el.parentElement).filter((p): p is HTMLElement => !!p));
            parents.forEach(parent => Array.from(parent.children).forEach(el => {
                if (keep.has(el) || el.id === 'wpadminbar' || !(el instanceof HTMLElement)) return;
                if (el.tagName === 'SCRIPT' || el.inert) return;
                el.inert = true;
                madeInert.push(el);
            }));
        } else {
            madeInert.splice(0).forEach(el => el.inert = false);
        }
    };

    // Focus: into the panel on open, back to the hamburger on close (if focus was in the panel, or Escape).
    document.addEventListener('datatoggle', (e) => {
        const {classNames, active, source} = (e as CustomEvent<DataToggleEventDetail>).detail;
        if (!classNames.includes('is-menu-open')) return;

        setPageInert(active);
        if (active) {
            panelFocusables()[0]?.focus();
        } else if (source === 'escape' || panel.contains(document.activeElement)) {
            hamburger.focus();
        }
    });

    // A link in the panel closes it (also anchor links to this page).
    panel.addEventListener('click', (e) => {
        if ((e.target as Element).closest('a[href]')) close();
    });

    // Past the breakpoint the hamburger is hidden: nothing could close the panel any more.
    window.addEventListener('resize', () => {
        if (isOpen() && !isVisible(hamburger)) close();
    }, {passive: true});
}

/**
 * Slide a panel submenu open or closed: CSS has height 0 / auto, which cannot transition,
 * so the height goes through pixels (current → measured) and back to auto when done.
 */
function slide(submenu: HTMLElement, open: boolean, apply: () => void): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        apply();
        return;
    }
    submenu.style.height = `${submenu.getBoundingClientRect().height}px`;
    apply(); // is-open on or off: the CSS height under the inline one
    const target = open ? submenu.scrollHeight : 0;

    submenu.getBoundingClientRect(); // commit the start height before the change
    submenu.style.height = `${target}px`;

    const done = (e: TransitionEvent) => {
        if (e.target !== submenu || e.propertyName !== 'height') return;
        submenu.style.height = '';
        submenu.removeEventListener('transitionend', done);
    };
    submenu.addEventListener('transitionend', done);
}

/**
 * Submenu buttons (.nav-menu__toggle, from px_nav_menu()): open/close their item (li.is-open).
 * On the bar one submenu per level is open at a time; a click outside closes them, Escape the deepest. Escape also
 * closes one shown by hover or focus (li.is-dismissed) until the pointer or focus leaves the item.
 * In the panel the submenu slides.
 */
function initSubmenus(): void {
    const setOpen = (item: Element, open: boolean) => {
        const apply = () => {
            item.classList.toggle('is-open', open);
            item.querySelector(':scope > .nav-menu__toggle')?.setAttribute('aria-expanded', String(open));
        };
        const submenu = item.querySelector<HTMLElement>(':scope > .sub-menu');
        if (submenu && item.closest('.nav-menu--panel')) {
            slide(submenu, open, apply);
        } else {
            apply();
        }
    };
    const closeBar = (except?: Element) => {
        document.querySelectorAll('.nav-menu--bar .is-open').forEach(item => {
            if (item !== except && !item.contains(except ?? null)) setOpen(item, false);
        });
    };

    document.addEventListener('click', (e) => {
        const button = (e.target as Element).closest('.nav-menu__toggle');
        if (!button) {
            if (!(e.target as Element).closest('.nav-menu--bar')) closeBar();
            return;
        }
        const item = button.parentElement;
        if (!item) return;

        const open = !item.classList.contains('is-open');
        if (open && item.closest('.nav-menu--bar')) closeBar(item);
        if (open) item.classList.remove('is-dismissed');
        setOpen(item, open);
    });

    // Escape closes one level at a time, the deepest shown first; focus goes back to its button.
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const shown = document.querySelectorAll('.nav-menu--bar li:is(:hover, :focus-within, .is-open):has(> .sub-menu):not(.is-dismissed)');
        const item = shown[shown.length - 1]; // nested items come after their parent
        if (!item) return;
        item.classList.add('is-dismissed');
        if (item.classList.contains('is-open')) setOpen(item, false);
        if (item.querySelector(':scope > .sub-menu')?.contains(document.activeElement)) {
            item.querySelector<HTMLElement>(':scope > .nav-menu__toggle')?.focus();
        }
    });

    // A dismissed dropdown shows again once the pointer or focus has left its item.
    document.querySelectorAll('.nav-menu--bar li').forEach(item => {
        item.addEventListener('mouseleave', () => item.classList.remove('is-dismissed'));
        item.addEventListener('focusout', (e) => {
            if (!item.contains((e as FocusEvent).relatedTarget as Node | null)) item.classList.remove('is-dismissed');
        });
    });
}

/**
 * Hover pill on the bar's submenus and the language list: one blue glass pill per list (li.nav-menu__pill, appended so the items'
 * stagger order stays) glides to the item under the mouse or with keyboard focus, and fades out when both
 * leave; the link it is on gets .is-pilled (white text). It appears in place (no glide) the first time. Offsets, not rects: the card is scaled while it opens.
 */
function initSubmenuPill(): void {
    document.querySelectorAll<HTMLElement>('.nav-menu--bar .sub-menu, .px-lang__list').forEach(submenu => {
        const pill = document.createElement('li');
        pill.className = 'nav-menu__pill';
        pill.setAttribute('role', 'presentation');
        pill.setAttribute('aria-hidden', 'true');
        submenu.append(pill);

        // The submenu's own item link (a nested submenu's links have their own pill).
        const linkOf = (target: EventTarget | null): HTMLElement | null => {
            const link = (target as Element | null)?.closest<HTMLElement>('a, .nav-menu__toggle--label');
            return link?.parentElement?.parentElement === submenu ? link : null;
        };
        let pilled: HTMLElement | null = null;
        const moveTo = (link: HTMLElement) => {
            pilled?.classList.remove('is-pilled');
            pilled = link;
            link.classList.add('is-pilled');
            const item = link.parentElement as HTMLElement;
            const shown = pill.classList.contains('is-shown');
            if (!shown) pill.style.transition = 'none';
            pill.style.transform = `translate(${item.offsetLeft + link.offsetLeft}px, ${item.offsetTop + link.offsetTop}px)`;
            pill.style.width = `${link.offsetWidth}px`;
            pill.style.height = `${link.offsetHeight}px`;
            if (!shown) {
                pill.getBoundingClientRect(); // commit the place before the fade in
                pill.style.transition = '';
                pill.classList.add('is-shown');
            }
        };
        const hide = () => {
            pill.classList.remove('is-shown');
            pilled?.classList.remove('is-pilled');
            pilled = null;
        };

        submenu.addEventListener('pointerover', (e) => {
            if (e.pointerType === 'touch') return;
            const link = linkOf(e.target);
            if (link) moveTo(link);
        });
        submenu.addEventListener('pointerleave', () => {
            if (!submenu.contains(document.activeElement)) hide();
        });
        submenu.addEventListener('focusin', (e) => {
            const link = linkOf(e.target);
            if (link) moveTo(link);
        });
        submenu.addEventListener('focusout', (e) => {
            if (!submenu.contains(e.relatedTarget as Node | null) && !submenu.matches(':hover')) hide();
        });
    });
}

/**
 * Language pill (.px-lang--hover; list mode has no button): its button opens the list (aria-expanded); a click outside, focus leaving it or
 * Escape closes it (Escape puts focus back on the button). On the bar it also shows on hover/focus (header.css).
 */
function initLanguage(): void {
    const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('.px-lang__button'));
    const set = (button: HTMLButtonElement, open: boolean) => button.setAttribute('aria-expanded', String(open));

    buttons.forEach(button => {
        const wrapper = button.parentElement;
        button.addEventListener('click', () => set(button, button.getAttribute('aria-expanded') !== 'true'));
        wrapper?.addEventListener('focusout', (e) => {
            if (!wrapper.contains((e as FocusEvent).relatedTarget as Node | null)) set(button, false);
        });
    });

    document.addEventListener('click', (e) => {
        buttons.forEach(button => {
            if (!button.parentElement?.contains(e.target as Node)) set(button, false);
        });
    });

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const open = buttons.find(button => button.getAttribute('aria-expanded') === 'true');
        if (!open) return;
        set(open, false);
        open.focus();
    });
}
